import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { init } from 'contentful-ui-extensions-sdk';
import {
  Form,
  FieldGroup,
  Button,
  Flex,
  Option,
  Card,
  Typography,
  Paragraph,
  SectionHeading
} from '@contentful/forma-36-react-components';
import '@contentful/forma-36-react-components/dist/styles.css';
import './index.css';
import './App.scss';
import { mappingRowTypes } from './helpers/constants';
import { IAttributeMapping, IAttributeValueMapping } from './helpers/interfaces';
import { MappingRow } from './components/mappingRow';
import { getAttributeValues, AttributesValues } from './clients/DiscoveryPagesServiceClient';
import { getAcceptableAttributesFromSource, getAttributeValuesFromSource } from './manager/AttributeManager';

export const App = ({ sdk }) => {
  const [mapping, setMapping] = useState<IAttributeMapping>({
    source: '',
    target: '',
    attributeValueMappings: []
  });
  const [attributeSource, setAttributeSource] = useState<AttributesValues>({} as AttributesValues);
  const [attributeValues, setAttributeValues] = useState<string[]>([]);
  const [newAttributeValueMapping, setNewAttributeValueMapping] = useState<IAttributeValueMapping>({
    source: '',
    target: undefined
  });

  const [attributeOptions, setAttributeOptions] = useState<JSX.Element[]>([]);
  const [attributeValueOptions, setAttributeValueOptions] = useState<JSX.Element[] | null>([]);

  useEffect(() => {
    sdk.window?.startAutoResizer();
    const persistedMapping = sdk.field?.getValue();
    if (persistedMapping) {
      setMapping(persistedMapping);
    }

    const fetchAttributeWithValues = async () => {
      const attributeWithValues = await getAttributeValues();
      if (attributeWithValues) {
        setAttributeSource(attributeWithValues);
      } else {
        console.log('Failed to retrieve attributes with values from Discovery Pages Service');
      }
    };

    fetchAttributeWithValues();
  }, []);

  useEffect(() => {
    const mapAttributeOptions = (): JSX.Element[] => {
      return buildOptions(getAcceptableAttributesFromSource(attributeSource));
    };

    setAttributeOptions(mapAttributeOptions());
  }, [attributeSource]);

  useEffect(() => {
    const attributeValuesSorted = getAttributeValuesFromSource(attributeSource, mapping.target);
    setAttributeValues(attributeValuesSorted);
  }, [mapping, attributeSource]);

  useEffect(() => {
    const getAttributeValueOptions = (): JSX.Element[] | null =>
      attributeValues ? buildOptions(attributeValues) : null;

    setAttributeValueOptions(getAttributeValueOptions());
  }, [attributeValues]);

  const buildOptions = (optionValues: string[]): JSX.Element[] => {
    return optionValues.map((value) => {
      return (
        <Option key={value} value={value}>
          {value}
        </Option>
      );
    });
  };

  const getMappingIndex = (id) => {
    const idPattern = /^.*_\d+$/g;
    const lastNumberInId = /\d+$/g;
    if (!id.match(idPattern)) {
      return -1;
    }
    return id.match(lastNumberInId)[0];
  };

  const onSourceAttributeValueChange = (event) => {
    if (!event.target.value || event.target.value.length === 0) {
      return;
    }
    const mappingIndex = getMappingIndex(event.target.id);

    if (mappingIndex >= 0) {
      const newMapping = { ...mapping };
      newMapping.attributeValueMappings[mappingIndex].source = event.target.value;
      setMapping(newMapping);
    } else {
      if (!newAttributeValueMapping) {
        const newValue = { source: event.target.value, target: undefined };
        setNewAttributeValueMapping(newValue);
      } else {
        const updatedValue = { ...newAttributeValueMapping };
        updatedValue.source = event.target.value;
        setNewAttributeValueMapping(updatedValue);
      }
    }
  };

  const onTargetAttributeValueChange = (event) => {
    const mappingIndex = getMappingIndex(event.target.id);

    if (mappingIndex >= 0) {
      const newMapping = { ...mapping };
      newMapping.attributeValueMappings[mappingIndex].target = event.target.value;
      setMapping(newMapping);
    } else {
      if (newAttributeValueMapping) {
        const updatedValue = { ...newAttributeValueMapping };
        updatedValue.target = event.target.value;
        setNewAttributeValueMapping(updatedValue);
      }
    }
  };

  const onRemoveAttributeValueMappingClick = (buttonId) => {
    const mappingIndex = getMappingIndex(buttonId);

    if (mappingIndex >= 0) {
      const newMapping = { ...mapping };
      newMapping.attributeValueMappings.splice(mappingIndex, 1);
      setMapping(newMapping);
    }
  };

  const saveMapping = () => {
    if (newAttributeValueMapping && newAttributeValueMapping.source && newAttributeValueMapping.target) {
      const newMapping = { ...mapping };
      if (!newMapping.attributeValueMappings) {
        newMapping.attributeValueMappings = [];
      }
      newMapping.attributeValueMappings.push(newAttributeValueMapping);
      setNewAttributeValueMapping({
        source: '',
        target: ''
      });
      setMapping(newMapping);
    }

    sdk.field.setValue(mapping).catch((err) => {
      console.log(`Error saving mapping: ${JSON.stringify(err)}`);
    });
  };

  const onSourceAttributeChange = (event) => {
    mapping.source = event.target.value;
  };

  const onTargetAttributeChange = (event) => {
    mapping.target = event.target.value;
    setAttributeValues(getAttributeValuesFromSource(attributeSource, mapping.target));
  };

  return (
    <>
      <Form onSubmit={() => saveMapping()} spacing="default">
        <FieldGroup>
          <Typography>
            <SectionHeading>Current Attribute Mapping</SectionHeading>
          </Typography>
          <MappingRow
            mapping={mapping}
            options={attributeOptions}
            onSourceChange={onSourceAttributeChange}
            onTargetChange={onTargetAttributeChange}
            mappingRowType={mappingRowTypes.ATTRIBUTE}
            isRequired={true}
          />
        </FieldGroup>
        <FieldGroup>
          <Typography>
            <SectionHeading>Current Attribute Value Mappings</SectionHeading>
          </Typography>
          <Card title="Current Attribute Value Mappings">
            {mapping.attributeValueMappings.map((attributeValueMapping, index) => {
              return (
                <MappingRow
                  key={`attribute_value_mapping_${index}`}
                  mapping={attributeValueMapping}
                  options={attributeValueOptions}
                  onSourceChange={onSourceAttributeValueChange}
                  onTargetChange={onTargetAttributeValueChange}
                  onRemoveClick={onRemoveAttributeValueMappingClick}
                  mappingRowType={mappingRowTypes.ATTRIBUTE_VALUE}
                  isRequired={true}
                  index={index}
                />
              );
            })}
          </Card>
          <Typography>
            <SectionHeading>New Attribute Value Mapping</SectionHeading>
          </Typography>
          <Card title="New Attribute Value Mapping">
            <MappingRow
              mapping={newAttributeValueMapping}
              options={attributeValueOptions}
              onSourceChange={onSourceAttributeValueChange}
              onTargetChange={onTargetAttributeValueChange}
              mappingRowType={mappingRowTypes.NEW_ATTRIBUTE_VALUE_MAPPING}
              isRequired={false}
            />
            <Typography key="new_attribute_value_mapping">
              <Flex justifyContent="space-between" alignItems="center" marginTop="spacingM">
                <Flex>&nbsp; </Flex>
                <Flex alignItems="center">
                  <Flex marginRight="spacingM">
                    <Paragraph>&nbsp;</Paragraph>
                  </Flex>
                  <Flex>
                    <Button buttonType="primary" onClick={saveMapping}>
                      Save new attribute value mapping
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            </Typography>
          </Card>
        </FieldGroup>
        <FieldGroup>
          <Button type="submit">Save changes</Button>
        </FieldGroup>
      </Form>
    </>
  );
};

App.propTypes = {
  sdk: PropTypes.object.isRequired
};

init((sdk) => {
  ReactDOM.render(<App sdk={sdk} />, document.getElementById('root'));
});
